import React, { Fragment, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

// 2026 application deadlines. Sources: CTCAC 2026 Application Information (9%)
// and the CDLAC 2026 QRRP schedule (4% / tax-exempt bond). Update annually when
// the new calendars are published.
const EVENTS = [
    { date: '2026-02-03', program: '4% / Bond', title: 'CDLAC QRRP Round 1 — Applications due', detail: 'Tax-exempt bond / 4% credit. Awards meeting May 12, 2026.' },
    { date: '2026-04-07', program: '9% Round 1', title: '2026 First Round — Applications due', detail: 'Submitted by 5:00 p.m. PT.' },
    { date: '2026-05-19', program: '4% / Bond', title: 'CDLAC QRRP Round 2 — Applications due', detail: 'Tax-exempt bond / 4% credit. Awards meeting Aug 18, 2026.' },
    { date: '2026-07-21', program: '9% Round 2', title: '2026 Second Round — Applications due', detail: 'Submitted by 5:00 p.m. PT.' },
    { date: '2026-09-08', program: '4% / Bond', title: 'CDLAC QRRP Round 3 — Applications due', detail: 'Tax-exempt bond / 4% credit. Awards meeting Nov 10, 2026.' },
];

const fmtDate = (iso) => {
    const d = new Date(iso + 'T17:00:00-07:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
};

const CtcacDeadlinesPage = () => {
    usePageSeo({
        title: 'CTCAC & CDLAC 2026 Deadline Calendar — 9% and 4% Rounds',
        description: 'CTCAC and CDLAC 2026 deadline calendar for California LIHTC rounds — 9% competitive and 4% / tax-exempt bond (QRRP) — with a live countdown to the next due date.',
        keywords: 'CTCAC 2026 deadlines, CDLAC QRRP schedule, LIHTC application dates California, 9% tax credit round dates, 4% bond application deadline, tax-exempt bond rounds, CTCAC calendar, affordable housing schedule, BUILDLAB',
    });

    const { upcoming, list } = useMemo(() => {
        const now = new Date();
        const enriched = EVENTS.map((e) => {
            const due = new Date(e.date + 'T17:00:00-07:00');
            const days = Math.ceil((due - now) / 86400000);
            return { ...e, due, days, past: days < 0 };
        }).sort((a, b) => a.due - b.due);
        const next = enriched.find((e) => !e.past) || null;
        return { upcoming: next, list: enriched };
    }, []);

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'CTCAC & CDLAC 2026 Deadline Calendar'} pagesub={'Calendar'} pageTop={'Affordable Housing'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">When are the 2026 tax-credit deadlines?</h2>
                                <p>
                                    The 9% competitive credit runs in two rounds a year — and readiness points reward projects that
                                    have site control, entitlements, and financing locked in <em>before</em> they apply. Track the
                                    dates below, then make sure your technology scope (broadband, WiFi, security, accessibility) is
                                    designed early so it never becomes the thing that delays you.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Countdown */}
                            <div className="col-lg-5 mb-4">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', position: 'sticky', top: 100 }}>
                                    {upcoming ? (
                                        <>
                                            <div style={{ fontSize: '.85rem', opacity: 0.8 }}>Next deadline · {upcoming.program}</div>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '2px 0 4px' }}>
                                                <span style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1, color: upcoming.days <= 30 ? '#FFC9A3' : '#7CFFB2' }}>{upcoming.days}</span>
                                                <span style={{ fontSize: '1.1rem', opacity: 0.7 }}>days left</span>
                                            </div>
                                            <div style={{ fontWeight: 700, marginBottom: 6 }}>{upcoming.title}</div>
                                            <p style={{ fontSize: '.9rem', opacity: 0.9 }}>{fmtDate(upcoming.date)} · {upcoming.detail}</p>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ fontSize: '.85rem', opacity: 0.8 }}>2026 rounds closed</div>
                                            <p style={{ fontSize: '.92rem', opacity: 0.9, marginTop: 6 }}>
                                                The 2026 competitive rounds have passed. The 2027 calendar is typically published late in
                                                the prior year — check the CTCAC site for the new dates.
                                            </p>
                                        </>
                                    )}
                                    <Link to="/contact" className="btn btn-primary w-100 mt-3" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Get application-ready">Get application-ready</span>
                                    </Link>
                                    <ToolLeadCapture
                                        tool="CTCAC Deadline Calendar"
                                        summary={upcoming ? `Next deadline: ${upcoming.program} in ${upcoming.days} days — ${upcoming.title}` : '2026 competitive rounds closed'}
                                        emailLabel="Email me reminders"
                                    />
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="col-lg-7 mb-4">
                                {list.map((e) => (
                                    <div key={e.date} style={{
                                        background: '#fff', border: '1px solid var(--bs-border-color)', borderLeft: `4px solid ${e.past ? '#ced4da' : 'var(--bs-primary)'}`,
                                        borderRadius: 12, padding: '18px 22px', marginBottom: 14, opacity: e.past ? 0.6 : 1,
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                                            <div>
                                                <span style={{ fontSize: '.75rem', fontWeight: 700, color: 'var(--bs-primary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{e.program}</span>
                                                <h3 className="m-0" style={{ fontSize: '1.05rem' }}>{e.title}</h3>
                                            </div>
                                            <span style={{ fontSize: '.85rem', color: e.past ? '#6c757d' : 'var(--bs-primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                {e.past ? 'Closed' : `${e.days} days`}
                                            </span>
                                        </div>
                                        <p className="m-0 mt-1" style={{ fontSize: '.88rem', color: '#555' }}>{fmtDate(e.date)} · {e.detail}</p>
                                    </div>
                                ))}
                                <div style={{ background: 'var(--bs-light)', borderRadius: 12, padding: '16px 22px' }}>
                                    <h3 className="m-0" style={{ fontSize: '1rem' }}>4% credits &amp; bond financing</h3>
                                    <p className="m-0 mt-1" style={{ fontSize: '.88rem', color: '#555' }}>
                                        4% tax-credit / tax-exempt bond deals run through CDLAC's Qualified Residential Rental Project
                                        (QRRP) program in scheduled rounds (shown above). Confirm the full schedule and awards-meeting
                                        dates on the{' '}
                                        <a href="https://www.treasurer.ca.gov/cdlac/applications/qrrp/index.asp" target="_blank" rel="noopener noreferrer">official CDLAC QRRP calendar</a>.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            Source: CTCAC 2026 Application Information (California State Treasurer). Dates are set by CTCAC and change
                            each year — always confirm the current deadlines and exact submission instructions on the official CTCAC
                            website before relying on them. BUILDLAB is not affiliated with CTCAC; we help developers get
                            application-ready by designing the technology scope early.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default CtcacDeadlinesPage;
